"use client";

import { MessageChannel } from "@prisma/client";
import { useEffect, useRef, useState } from "react";
import ActionMenu from "@/app/(components)/generics/ActionMenu/ActionMenu";
import CopyButton from "@/app/(components)/generics/CopyButton/CopyButton";
import IconCopy from "@/app/(components)/generics/Icons/IconCopy";
import IconEdit from "@/app/(components)/generics/Icons/IconEdit";
import IconRestart from "@/app/(components)/generics/Icons/IconRestart";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import CircleLoader from "@/app/(components)/generics/loaders/CircleLoader/CircleLoader";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { PageParams } from "@/app/types/common/DefaultProps";
import {
  MessageSequenceStepData,
  MessageSequenceStepVariantData,
} from "@/app/types/messages-content/MessageSequenceData";
import { USequenceVariant } from "@/app/utils/USequenceVariant";
import { UStyles } from "@/app/utils/UStyles";
import {
  getDelayLabel,
  getStepIcon,
  getStepLabel,
  getTemplateSkeleton,
  resolveCurrentVariant,
} from "./MessageSequence.logic";
import styles from "./MessageSequence.module.css";
import SequenceVariantTabs from "./SequenceVariantTabs";

type Props = {
  params: PageParams;
  step: MessageSequenceStepData;
  isLast?: boolean;
  isPlaceholder?: boolean;
  canAddVariant?: boolean;
  regeneratingVariantIds?: number[];
  onEdit?: () => void;
  onDelete?: () => void;
  onAddVariant?: () => void;
  onEditVariant?: (variant: MessageSequenceStepVariantData) => void;
  onRegenerateVariant?: (variant: MessageSequenceStepVariantData) => void;
  onDeleteVariant?: (variant: MessageSequenceStepVariantData) => void;
  channel: MessageChannel;
};

export default function SequenceStepRow(props: Props) {
  const T = useT();
  const {
    params,
    step,
    channel,
    isLast = false,
    isPlaceholder = false,
    canAddVariant = false,
    regeneratingVariantIds,
    onEdit,
    onDelete,
    onAddVariant,
    onEditVariant,
    onRegenerateVariant,
    onDeleteVariant,
  } = props;

  const StepIcon = getStepIcon(step.type);

  const [selectedId, setSelectedId] = useState<number | null>(() =>
    USequenceVariant.resolve(
      channel,
      step.type,
      step.variants.map((item) => item.id),
    ),
  );
  const variant = resolveCurrentVariant(step.variants, selectedId);

  const selectVariant = (variantId: number) => {
    setSelectedId(variantId);
    USequenceVariant.select(channel, step.type, variantId);
  };

  const prevCount = useRef(step.variants.length);
  useEffect(() => {
    const count = step.variants.length;
    if (count > prevCount.current) {
      setSelectedId(step.variants[count - 1].id);
    }
    prevCount.current = count;
  }, [step.variants]);
  const isRegenerating = !!variant && !!regeneratingVariantIds?.includes(variant.id);

  const hasActions = !!(
    onEdit ||
    onDelete ||
    onEditVariant ||
    onRegenerateVariant ||
    onDeleteVariant
  );

  return (
    <div className={styles.step}>
      <div className={styles.stepTimeline}>
        <span className={styles.stepIcon}>
          <StepIcon />
        </span>
        {!isLast && <span className={styles.stepLine} />}
      </div>

      <div className={styles.stepTitle}>
        <span className={styles.stepName}>{getStepLabel(step.type, T)}</span>
        <span className={styles.stepDay}>{getDelayLabel(step.delay, step.delayUnit, T)}</span>
      </div>

      <div className={styles.stepBody}>
        {!isPlaceholder && (
          <SequenceVariantTabs
            variants={step.variants}
            currentId={variant?.id ?? 0}
            canAdd={canAddVariant}
            onSelect={selectVariant}
            onAdd={onAddVariant}
            actions={
              hasActions && variant ? (
                <ActionMenu
                  items={[
                    (onEditVariant || onRegenerateVariant || onDeleteVariant) && {
                      isTitle: true,
                      label: T(TKey.MESSAGES_CONTENT_MENU_VARIANT_SECTION) as string,
                    },
                    onEditVariant && {
                      label: T(TKey.MESSAGES_CONTENT_EDIT_VARIANT) as string,
                      icon: <IconEdit width={16} height={16} />,
                      onClick: () => onEditVariant(variant),
                    },
                    onRegenerateVariant && {
                      label: T(TKey.MESSAGES_CONTENT_REGENERATE_STEP) as string,
                      icon: <IconRestart size={16} />,
                      onClick: () => onRegenerateVariant(variant),
                    },
                    onDeleteVariant &&
                      step.variants.length > 1 && {
                        label: T(TKey.MESSAGES_CONTENT_DELETE_VARIANT) as string,
                        icon: <IconTrash size={16} />,
                        danger: true,
                        onClick: () => onDeleteVariant(variant),
                      },
                    (onEdit || onDelete) && { isDivider: true },
                    (onEdit || onDelete) && {
                      isTitle: true,
                      label: T(TKey.MESSAGES_CONTENT_MENU_STEP_SECTION) as string,
                    },
                    onEdit && {
                      label: T(TKey.MESSAGES_CONTENT_EDIT_STEP) as string,
                      icon: <IconEdit width={16} height={16} />,
                      onClick: onEdit,
                    },
                    onDelete && {
                      label: T(TKey.MESSAGES_CONTENT_DELETE_STEP) as string,
                      icon: <IconTrash size={16} />,
                      danger: true,
                      onClick: onDelete,
                    },
                  ]}
                />
              ) : undefined
            }
          />
        )}

        <div className={styles.templateWrap}>
          {isPlaceholder ? (
            <div className={styles.stepTemplate}>
              <div className={styles.templateSkeleton}>
                {getTemplateSkeleton(step.type).map((width, index) => (
                  <span key={index} className={styles.bar} style={{ width }} />
                ))}
              </div>
            </div>
          ) : isRegenerating ? (
            <div className={UStyles.classNames(styles.stepTemplate, styles.templateLoading)}>
              <CircleLoader size={20} color="var(--color-green-3)" />
              <span className={styles.loadingText}>{T(TKey.MESSAGES_CONTENT_GENERATING)}</span>
            </div>
          ) : (
            <p className={styles.stepTemplate}>{variant?.template}</p>
          )}

          {!isPlaceholder && !isRegenerating && variant && (
            <div className={styles.copySlot}>
              <CopyButton
                params={params}
                value={variant.template}
                button={
                  <span className={styles.copyBtn}>
                    <IconCopy size={14} />
                  </span>
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
